import { Stack, Typography, Skeleton } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageStyle } from '../../../../assets/styles/custom.styles';
import { axiosInstance } from '../../../../assets/utils/axiosInstance';
import getErrorMessage from '../../../../assets/utils/getErrorMessage';
import CustomerUserInfo from '../../../../components/partner/customer/customerUserInfo';
import ErrorResponseObject from '../../../../interfaces/errorResponseObject';
import RequestStatusObject from '../../../../interfaces/RequestStatusObject';

export interface TenantUserDetailObject {
  contactNumber: string | null;
  dateRegistered: string;
  email: string;
  fullName: string;
  lastActive: string;
  mfaStatus: string;
  totpStatus: string;
  tenantUserRole: string;
}

interface TenantUserDetailFromAPIObject {
  contact_number: string | null;
  date_registered: string;
  email: string;
  full_name: string;
  last_active: string;
  is_mfa_enabled: string;
  is_totp_enabled: string;
  tenant_user_role: string;
}

const transformTenantUserDetailData = (data: TenantUserDetailFromAPIObject) => {
  const userDetails: TenantUserDetailObject = {
    contactNumber: data.contact_number,
    dateRegistered: data.date_registered,
    email: data.email,
    fullName: data.full_name, 
    lastActive: data.last_active,
    mfaStatus: data.is_mfa_enabled,
    totpStatus: data.is_totp_enabled,
    tenantUserRole: data.tenant_user_role,
  };
  return userDetails;
};

const slugToName = (slug: string) => {
  return slug.split('-').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const headerText = {
  color: '#181818',
  fontStyle: 'normal',
  fontSize: '22px',
  fontWeight: 600, 
  lineHeight: 'normal',
};
const indicatorText = {
  color: '#878787',
  fontStyle: 'normal',
  fontSize: '14px',
  fontWeight: 500, 
  lineHeight: 'normal',
};

const CustomerUserInfoPage = () => {
  const { slug, userId } = useParams();
  const [userData, setUserData] = useState<TenantUserDetailObject>({
    contactNumber: null,
    dateRegistered: '',
    email: '',
    fullName: '', 
    lastActive: '',
    mfaStatus: '',
    totpStatus: '',
    tenantUserRole: '',
  });
  const [requestStatus, setRequestStatus] = useState<RequestStatusObject>({
    isLoading: false,
    success: false,
    error: false,
    responseText: '',
  });

  const getTenantUserDetails = useCallback(async () => {
    setRequestStatus({
      isLoading: true,
      success: false,
      error: false,
      responseText: '',
    });
    try {
      const response = await axiosInstance().get(`/confide/tenants/${slug}/users/${userId}`);
      const data = response.data;
      const transformedData = transformTenantUserDetailData(data);
      setUserData(transformedData);
      setRequestStatus({
        isLoading: false,
        success: true,
        error: false,
        responseText: '',
      });
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err as ErrorResponseObject);
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        responseText: errorMessage,
      });
    }
  }, [slug, userId]);

  useEffect(() => {
    getTenantUserDetails();
  }, [getTenantUserDetails]);

  const updateEmail = (email: string) => {
    setUserData({ ...userData, email: email });
  };

  return (
    <Stack sx={PageStyle.container} spacing={3}>
      <Stack direction='row' spacing={5}>
        <Stack>
          {requestStatus.isLoading &&
            <Skeleton variant="text" sx={{ fontSize: '22px', width: '200px' }} /> 
          }
          {!requestStatus.isLoading && !requestStatus.error &&
            <Typography sx={headerText}>{slug && slugToName(slug)}</Typography>
          }
          <Typography sx={indicatorText}>Company</Typography>
        </Stack>
        <Stack>
          {requestStatus.isLoading &&
            <Skeleton variant="text" sx={{ fontSize: '22px', width: '200px' }} /> 
          }
          {!requestStatus.isLoading && !requestStatus.error &&
            <Typography sx={headerText}>{userData.fullName ? userData.fullName : '--'}</Typography>
          }
          <Typography sx={indicatorText}>User</Typography>
        </Stack>
      </Stack>
      <CustomerUserInfo 
        isLoading={requestStatus.isLoading}
        isError={requestStatus.error}
        errorText={requestStatus.responseText}
        data={userData}
        updateEmail={updateEmail}/>
    </Stack>
  );
};

export default CustomerUserInfoPage;